import { ModalBigCentered } from '../../Modal'
import { ModalOpenedSetTy } from '../../Modal/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import TwinIcon from '../../TwinIcon'
import twinFetchPost from '../../../utils/globals/data'
import { TwinDictionary } from '../../../utils/globals/dictionary'
import { faMicrophone, faTimes } from '@fortawesome/pro-light-svg-icons'
import './modalai.sass'
import { ButtonPrimary } from '../../Button'
interface ModalAiProps extends ModalAiLogicProps {
    responseRendered?: JSX.Element
}

const ModalAi: React.FC<ModalAiProps> = ({setOpened, responseRendered, ...logic}) => {
    const { stopRecording, isRecording, isProcessing, error } = useModalAiLogic({ setOpened, ...logic })
    const {t} = useTwinTranslation()
    let translations: {title: string, icon?: JSX.Element, subtitle: string} = {
        title: t('activateMicOfYourExplorer', 'Activa el micrófono de tu navegador'),
        icon: <TwinIcon icon={faMicrophone} className='text-green-43 h-50 mb-20' />,
        subtitle: t('activateMicLong', 'Pulsa sobre permitir el micrófono en la parte superior izquierda de la pantalla para poder acceder a esta funcionalidad')
    }
    let renderThis: JSX.Element | null= null
    if(error){
        translations = {
            title: t('somethingWrong', 'Algo ha fallado'),
            subtitle: error
        }
    }else if (responseRendered){
        renderThis = responseRendered
        translations = {
            title: '',
            subtitle: ''
        }
    }else if(isProcessing){
        translations = {
            title: t('processing', 'Procesando'),
            subtitle: t('ourIaIsProcessing', 'Nuestra inteligencia artificial esta procesando tu audio, un momento porfavor...'),
            icon: <div className='mx-auto h-60 mb-20 items-center flex'><div className='modal_ai_loader'></div></div>
        }
    }else if(isRecording){
        translations = {
            title: t('listening', 'Escuchando'),
            subtitle: t('projectAiSubtitle', 'Haz un resumen de tu reunión con el cliente, y si quieres indica los siguientes pasos'),
            icon: <div className="mx-auto h-60 mb-20 items-center flex"><div className='modal_ai_pulse'></div></div>
        }
        renderThis = (
            <div className='mx-auto mt-30'>
                <ButtonPrimary onClick={() => stopRecording()}>{t('process', 'Procesar')}</ButtonPrimary>
            </div>
        )
    }
    return (
        <ModalBigCentered opened={true} setOpened={() => setOpened(null)} onClickOut={false}>
            <TwinIcon icon={faTimes} className='absolute right-9 top-9 cursor-pointer' onClick={() => setOpened(null)} />
            {translations.icon ? translations.icon : null}
            {translations.title ? <h2>{translations.title}</h2> : null}
            {translations.subtitle ? <span className='mt-15 regular16'>{translations.subtitle}</span> : null}
            {renderThis}
        </ModalBigCentered>
    )
}

interface ModalAiLogicProps {
    setOpened: ModalOpenedSetTy
    extraData: TwinDictionary
    url: string
    setResponse: React.Dispatch<React.SetStateAction<TwinDictionary | null>>
}

const MAX_DURATION = 5 * 60

const useModalAiLogic = ({extraData, url, setResponse}: ModalAiLogicProps) => {
    const [time, setTime] = useState(0);
    const [error, setError] = useState<null | string>(null)
    const [isRecording, setIsRecording] = useState(false)
    const [isProcessing, setIsProcessing] = useState(false)
    const streamRef = useRef<any>(null);
    const timerRef = useRef<any>(null);
    const mediaRecorderRef = useRef<any>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const {t} = useTwinTranslation()
    const invalidMessage = t('invalidMessageErr', 'No hemos podido transcribir correctamente tu mensaje. Comprueba que tu microfono funciona bien y vuelve a intentarlo.')

    const extraDataStr= JSON.stringify(extraData)

    const onRecordingStop = useCallback(async () => {
        const extraDataParsed = JSON.parse(extraDataStr)
        setIsProcessing(true)
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm;codecs=opus' });
        audioChunksRef.current = [];
        const formData = new FormData()
        formData.append('audio', blob)
        for (const key in extraDataParsed) {
            formData.append(key, extraDataParsed[key])   
        }
        const result = await twinFetchPost(url, formData, {type: 'special'})
        if(result.status === 200){
            const resultJson = await result.json()
            setResponse(resultJson)
        }else if(result.status === 422){
            setError(invalidMessage)
        }
    }, [url, extraDataStr, setResponse, invalidMessage])

    const stopRecording = useCallback((closing?: boolean) => {
        if (mediaRecorderRef.current?.state === 'recording' || mediaRecorderRef.current?.state === 'paused') {
            mediaRecorderRef.current.stop();
        }
        clearInterval(timerRef.current);
        if(!closing){
            onRecordingStop()
        }
    }, [onRecordingStop])

    const startTimer = useCallback(() => {
        timerRef.current = setInterval(() => {
            setTime(prev => {
                const newTime = prev + 1;
                if (MAX_DURATION && newTime >= MAX_DURATION) {
                    stopRecording();
                }
                return newTime;
            });
        }, 1000);
    }, [stopRecording])
    
    const startRecording = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
            }});
            streamRef.current = stream;

            const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
            mediaRecorderRef.current = mediaRecorder;

            mediaRecorder.ondataavailable = ({ data }) => {
                audioChunksRef.current.push(data);
            };

            mediaRecorder.onstop = () => {
                streamRef.current?.getTracks().forEach((track: any) => track.stop());
            };

            mediaRecorder.start(1000);
            startTimer()
            setIsRecording(true)
        } catch (error) {
            setError(invalidMessage)
        }
    }, [startTimer, invalidMessage])


    useEffect(() => {
        startRecording()
        return () => {
            stopRecording(true)
        }
    }, [startRecording, stopRecording])

    return { stopRecording, time, isRecording, isProcessing, error }
}

export default ModalAi